import { useInfiniteQuery } from '@tanstack/react-query';
import { getAllocationEventsTimeline } from '../api/sitesApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { AllocationEventsTimelineResponse } from '../types/allocationTimeline';

export const allocationTimelineKeys = {
  all: ['allocationTimeline'] as const,
  lists: () => [...allocationTimelineKeys.all, 'list'] as const,
  list: (
    siteId: number,
    plotId: number,
    allocationId: string,
    isAuthenticated: boolean
  ) =>
    [
      ...allocationTimelineKeys.lists(),
      { siteId, plotId, allocationId, isAuthenticated },
    ] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseAllocationEventsTimelineReturn {
  events: AllocationEventsTimelineResponse['items'];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch allocation events timeline with infinite scroll pagination
 */
export function useAllocationEventsTimeline(
  siteId: number | null,
  plotId: number | null,
  allocationId: string | null
): UseAllocationEventsTimelineReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: allocationTimelineKeys.list(
      siteId ?? 0,
      plotId ?? 0,
      allocationId ?? '',
      isAuthenticated
    ),
    queryFn: async ({ pageParam = 0 }) => {
      if (!siteId || !plotId || !allocationId) {
        throw new Error('siteId, plotId, and allocationId are required');
      }

      return await getAllocationEventsTimeline(siteId, plotId, allocationId, {
        skip: pageParam,
        take: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: lastPage => {
      return lastPage?.hasMore ? lastPage.skip + lastPage.take : undefined;
    },
    initialPageParam: 0,
    enabled:
      isAuthenticated &&
      siteId !== null &&
      siteId > 0 &&
      plotId !== null &&
      plotId > 0 &&
      allocationId !== null &&
      allocationId !== '',
    staleTime: 2 * 60 * 1000,
  });

  const events = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    events,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
